import { useState, useRef, useCallback } from 'react';
import { createClaim, buildClaimFormData } from '../../services/claimsService';
import { ApiError } from '../../services/api';
import type { Claim, ClaimActor, ClaimDraft } from '../../types/claims.types';

export interface SubmitResult {
  success: boolean;
  claim?: Claim | null;
  error?: string;
  fieldErrors?: Record<string, string[]>;
  retryAfter?: number;
}

export interface UseClaimSubmit {
  submit: (draft: ClaimDraft) => Promise<SubmitResult>;
  isSubmitting: boolean;
}

export function useClaimSubmit(actor: ClaimActor | null): UseClaimSubmit {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Genbrug samme nøgle ved retry; nulstil efter succes.
  const idempotencyKeyRef = useRef<string | null>(null);

  const submit = useCallback(
    async (draft: ClaimDraft): Promise<SubmitResult> => {
      if (!actor) return { success: false, error: 'Mangler salon-kontekst.' };
      if (!idempotencyKeyRef.current) idempotencyKeyRef.current = crypto.randomUUID();
      const key = idempotencyKeyRef.current;

      setIsSubmitting(true);
      try {
        const claim = await createClaim(buildClaimFormData(draft), key);
        idempotencyKeyRef.current = null; // succes → næste submit får ny nøgle
        return { success: true, claim };
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.statusCode === 429) {
            const secs = err.retryAfter ?? 30;
            return { success: false, error: `For mange forsøg. Prøv igen om ${secs} sek.`, retryAfter: secs };
          }
          if (err.statusCode === 422) {
            return { success: false, error: err.message, fieldErrors: err.fieldErrors };
          }
          return { success: false, error: err.message };
        }
        return { success: false, error: err instanceof Error ? err.message : 'Der opstod en fejl' };
      } finally {
        setIsSubmitting(false);
      }
    },
    [actor]
  );

  return { submit, isSubmitting };
}
